import React, { useState, useEffect, useMemo } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { SkinCard } from "./SkinCard.jsx";
import { MySkinControlCenter } from "./MySkinControlCenter.jsx";
import { SkeletonCard } from "./SkeletonCard.jsx";
import { InlineLoginSection } from "@/wrappers/AuthWrapper.jsx";
import _ from "lodash";

export const MySkinsContext = React.createContext({});
export const useMySkinsContext = () => React.useContext(MySkinsContext);

export const MySkins = () => {
  const skm_con = useSkinsMasterContext();
  const [my_skins_glob, set_my_skins_glob] = useState({});
  const [searchtxt, set_searchtxt] = useState("");
  const [selected_rvmode, set_selected_rvmode] = useState("");
  const [selected_rarity, set_selected_rarity] = useState("");
  const [selected_skin, set_selected_skin] = useState(null);

  const { owned_skins, skin_infomap, qo_skins_owned } = skm_con;
  const is_loading = qo_skins_owned?.isLoading || !owned_skins;

  const grouped_skins = useMemo(() => {
    if (!owned_skins || !skin_infomap) return [];

    const groups = {};

    owned_skins.forEach(skin => {
      const { rvsid, infoid } = skin;
      if (!infoid) return;

      if (!groups[infoid]) {
        const info = skin_infomap[infoid] || {};
        const baseskinid = infoid.split('-').slice(1).join('-'); // Remove rvmode prefix

        groups[infoid] = {
          infoid,
          baseskinid,
          info,
          rvmode: info.rvmode || infoid.split('-')[0],
          skins_n: 0,
          rvsids: []
        };
      }

      groups[infoid].skins_n++;
      groups[infoid].rvsids.push(rvsid);
    });

    return _.sortBy(Object.values(groups), 'baseskinid');
  }, [owned_skins, skin_infomap]);

  const filt_grouped_skins = useMemo(() => {
    let filtered = grouped_skins;

    // Search filter
    if (searchtxt.trim() && searchtxt.length >= 3) {
      const searchLower = searchtxt.toLowerCase().trim();
      filtered = filtered.filter(skin => {
        const skinName = skin.info?.skin || "";
        return skinName.toLowerCase().includes(searchLower);
      });
    }

    // RVMode filter
    if (selected_rvmode) {
      filtered = filtered.filter(skin => skin.rvmode === selected_rvmode);
    }

    // Rarity filter
    if (selected_rarity) {
      filtered = filtered.filter(skin => skin.info?.rarity === selected_rarity);
    }

    return filtered;
  }, [grouped_skins, searchtxt, selected_rvmode, selected_rarity]);

  // Filter toggle functions
  const toggle_rvmode = (rvmode) => {
    set_selected_rvmode(selected_rvmode === rvmode ? "" : rvmode);
  };

  const toggle_rarity = (rarity) => {
    set_selected_rarity(selected_rarity === rarity ? "" : rarity);
  };

  const my_skins_con = {
    my_skins_glob,
    set_my_skins_glob,
    grouped_skins,
    filt_grouped_skins,
    searchtxt,
    set_searchtxt,
    selected_rvmode,
    selected_rarity,
    selected_skin,
    set_selected_skin,
    toggle_rvmode,
    toggle_rarity,
  };

  // Show login prompt if not authenticated
  if (!skm_con.isauvault) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-slate-600/20 border border-slate-500/30 rounded-xl backdrop-blur-md">
          <InlineLoginSection
            customHeading="Login to View Your Skins"
            customDescription="Connect your wallet to access your skin collection, apply skins to your cores, and manage your inventory."
            customContent={
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="text-blue-400 font-digi text-sm text-center space-y-2">
                  <div className="font-bold">✨ My Skins Features:</div>
                  <div className="text-xs text-gray-300">
                    • View all your owned skins
                    <br />
                    • Apply skins to your cores
                    <br />
                    • Burn skins for SGC rewards
                    <br />
                    • Track skin rarity and details
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <MySkinsContext.Provider value={my_skins_con}>
      <div className="w-full">
        {/* Show Control Center if skin is selected */}
        {selected_skin ? (
          <MySkinControlCenter />
        ) : (
          <>
            {/* Search Bar and Filters */}
            <div className="mb-8 space-y-4">
          {/* Search Input */}
          <div className="max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search skins..."
              value={searchtxt}
              onChange={(e) => set_searchtxt(e.target.value)}
              className="w-full px-4 py-3 bg-slate-600/40 border border-slate-500/30 rounded-lg text-white placeholder-gray-400 backdrop-blur-md focus:outline-none"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-col items-center space-y-3">
            {/* RVMode Filters */}
            <div className="flex space-x-2">
              <span className="text-gray-400 text-sm mr-2">Vehicle:</span>
              {["bike", "car", "horse"].map((rvmode) => (
                <button
                  key={rvmode}
                  onClick={() => toggle_rvmode(rvmode)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    selected_rvmode === rvmode
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-slate-600/40 text-gray-300 hover:bg-slate-500/50"
                  }`}
                >
                  {rvmode.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Rarity Filters */}
            <div className="flex flex-wrap justify-center gap-2">
              <span className="text-gray-400 text-sm mr-2">Rarity:</span>
              {["common", "rare", "epic", "legendary", "ultimate", "exclusive"].map((rarity) => (
                <button
                  key={rarity}
                  onClick={() => toggle_rarity(rarity)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    selected_rarity === rarity
                      ? "bg-purple-600 text-white shadow-md"
                      : "bg-slate-600/40 text-gray-300 hover:bg-slate-500/50"
                  }`}
                >
                  {rarity.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Results Counter */}
        {searchtxt.trim() && searchtxt.length >= 3 && (
          <div className="text-center mb-6">
            <p className="text-sm text-gray-400">
              Found {filt_grouped_skins.length} types
            </p>
          </div>
        )}

            {/* Skins Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {is_loading ? (
                // Show skeleton cards while loading
                Array.from({ length: 6 }).map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))
              ) : (
                filt_grouped_skins.map((skin) => (
                  <SkinCard key={skin.infoid} skin={skin} />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </MySkinsContext.Provider>
  );
};

